/* iter161 — Client-side IndexedDB store for the user's Ride Mode
 * background image.
 *
 * Rules:
 *   • Blob (JPEG/PNG) is stored ONLY in IndexedDB on the user's device.
 *   • Never sent to the server; not synced across devices; wiped on
 *     device reset.
 *   • Presence of a saved user background makes it STATIC — the
 *     RideMode admin rotation is disabled until the user removes /
 *     replaces this image.
 *
 * API:
 *   • await setUserBackground(blob)
 *   • await getUserBackgroundUrl()  → object-URL string | null
 *   • await clearUserBackground()
 *   • subscribeUserBackground(cb)   → unsubscribe (fires on set/clear)
 */
const DB_NAME = "habano-ride-bg";
const STORE = "bg";
const KEY = "current";
const EVT = "habano:ride-bg-changed";

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("no indexeddb")); return; }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function put(blob) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(blob, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function get() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(KEY);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

async function del() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

let _cachedUrl = null;

export async function setUserBackground(blob) {
  await put(blob);
  if (_cachedUrl) { try { URL.revokeObjectURL(_cachedUrl); } catch { /* ignore */ } _cachedUrl = null; }
  try { window.dispatchEvent(new CustomEvent(EVT)); } catch { /* ignore */ }
}

export async function getUserBackgroundUrl() {
  const blob = await get().catch(() => null);
  if (!blob) return null;
  if (_cachedUrl) return _cachedUrl;
  _cachedUrl = URL.createObjectURL(blob);
  return _cachedUrl;
}

export async function clearUserBackground() {
  await del().catch(() => {});
  if (_cachedUrl) { try { URL.revokeObjectURL(_cachedUrl); } catch { /* ignore */ } _cachedUrl = null; }
  try { window.dispatchEvent(new CustomEvent(EVT)); } catch { /* ignore */ }
}

export function subscribeUserBackground(cb) {
  const handler = () => { cb?.(); };
  window.addEventListener(EVT, handler);
  return () => window.removeEventListener(EVT, handler);
}
